import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ActionPanelModule, ButtonModule, DatePickerModule, DropdownModule, InputModule, LinkModule, PageTitleModule, SwitchModule } from 'snl-complib';
import { CcdService } from 'src/app/services/ccd.service';
import { ToasterService } from 'src/app/services/toaster.service';
import { ROLES, TYPEOFCOA } from 'src/app/constant/constant';
import { DEPARTMENTS } from 'src/app/constant/table-headings';
import { ValidationService } from 'src/app/services/validation.service';
import { forkJoin } from 'rxjs';
import { StorageService } from 'src/app/services/storage.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { Toasts } from 'src/app/constant/toast.messages';
import { PreviewCcdDetailsComponent } from "../../components/preview-ccd-details/preview-ccd-details.component";
import { AuthService } from 'src/app/services/auth.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { ActivateDepartmentComponent } from 'src/app/components/activate-department/activate-department.component';

@Component({
  selector: 'app-manage-ccd',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DropdownModule, DatePickerModule, InputModule, ButtonModule, ActionPanelModule, PageTitleModule, LinkModule, PreviewCcdDetailsComponent, SwitchModule, SvgIconComponent, ActivateDepartmentComponent],
  templateUrl: './manage-ccd.component.html',
  styleUrl: './manage-ccd.component.scss'
})
export class ManageCcdComponent {
  public ccdRes: any = {};
  public ccdForm: FormGroup = new FormGroup({});
  public savedFormObj: any = [];
  public ownerAddedDocuments:any = [];
  private ccdNo: string | null = null;
  public typeOfCoa: any = TYPEOFCOA;
  public selectedCoaType: string = 'NEW_COA';
  public departmentFields: any[] = [];
  public openActivateDepartmentPopup: boolean = false;
  public submitLoading: boolean = false;
  public saveExitLoading: boolean = false;
  public fetchDataLoading: boolean = false;
  public editButtonRoute: string = '';
  public disableManagementSwitch: boolean = false;
  public autoSaveMessage: string = 'saved'; //not-saved, saved, saving
  public currentUser: any = {};
  public roles: any = ROLES;
  public newlyAddedDepartments: Array<string> = [];

  constructor(
    private ccdService: CcdService,
    private toasterService: ToasterService,
    private validationService: ValidationService,
    private storageService: StorageService,
    private navigationService: NavigationService,
    private route: ActivatedRoute,
    private router: Router,
    public authService: AuthService
  ) { }

  ngOnInit(): void {
    this.ccdNo = this.route.snapshot.queryParamMap.get('ccdNo');
    if (this.ccdNo) {
      this.fetchCurrentUserData();
      this.fetchData();
      this.editButtonRoute = `/dashboard/edit-ccd-details?ccdNo=${this.ccdNo}`;
      //set header data
      let queryParamsString:string =`?ccdNo=${this.ccdNo}`;
      const headerData = {
        headerTitle: 'Manage CCD',
        backButtonNavigateTo: `dashboard/review-ccd${queryParamsString}`,
        breadCrumbList: [
          { id: '1', label: 'Dashboard', navigateTo:'dashboard',isClickable: true },
          { id: '3', label: 'Manage CCD', isClickable: false }
        ]
      }
      this.navigationService.setHeaderData(headerData);
    }
  }

  isOwner() {
    return this.currentUser?.role === this.roles['Owner'];
  }
  isManager() {
    return this.currentUser?.role === this.roles['Manager'];
  }

  fetchCurrentUserData() {
    this.authService.getBackendUserData().subscribe((data: any) => {
      if (data) {
        this.currentUser = data;
      }
    });
  }

  fetchData() {
    const fetchCcdData$ = this.ccdService.fetchCcdData({ ccdNo: this.ccdNo });
    const fetchCcdNotes$ = this.ccdService.fetchCcdNotes({ ccdNo: this.ccdNo, department: 'OWNER' });

    this.fetchDataLoading = true;
    forkJoin([fetchCcdData$, fetchCcdNotes$]).subscribe({
      next: ([ccdData, ccdNotes]: any) => {
        this.ccdRes = ccdData?.ccdRes?.[0];
        this.ownerAddedDocuments = ccdData?.ownerAddedDocuments;
        if (this.ccdRes?.coaType === 'NEW_RATES_AND_TERMS' && this.ccdRes?.isNewClause === true) {
          this.selectedCoaType = 'NEW_RATES_AND_TERMS_WITH_NEW_CLAUSE';
        } else {
          this.selectedCoaType = this.ccdRes?.coaType;
        }
        this.makeCcdForm();
        this.populateCcdNotes(ccdNotes?.notes);
        this.fetchDataLoading = false;
      },
      error: (error) => {
        this.fetchDataLoading = false;
        this.toasterService.openToastCcd(Toasts.Types.Error, Toasts.Actions.GeneralError.Error, Toasts.Actions.GeneralError.Title);
        console.error('Error fetching data:', error);
      }
    });
  }

  makeCcdForm() {
    let departmentList: any = [];
    if(this.isManager()){
      if(this.authService?.isDepartmentalDependency){
        departmentList = DEPARTMENTS;
      }
      else{
        departmentList = DEPARTMENTS.filter((field: any) => field.visibleIn.includes(this.selectedCoaType));
      }
    }
    else if(this.isOwner()){
      departmentList = DEPARTMENTS.filter((field: any) => field.visibleIn.includes(this.selectedCoaType));
    }

    this.departmentFields = departmentList.map((field: any) => ({
      ...field,
      switchState: field.visibleIn.includes(this.selectedCoaType),
      realTimeSwitchState: field.visibleIn.includes(this.selectedCoaType),
    }));
  
    const formControls: { [key: string]: FormControl } = {};
    this.departmentFields.forEach(field => {
      formControls[field.fieldName] = new FormControl('', [Validators.minLength(3), Validators.maxLength(100)]);
    });
    Object.keys(formControls).forEach(key => {
      this.ccdForm.addControl(key, formControls[key]);
    });
  }

  populateCcdNotes(ccdNotesObj: any = {}) {
    Object.keys(ccdNotesObj).forEach(key => {
      if (this.ccdForm.get(key)) {
        this.ccdForm.get(key)?.patchValue(ccdNotesObj?.[key]?.notes);
        this.departmentFields = this.departmentFields.map((field: any) => {
          if (field.fieldName === key) {
            field.realTimeSwitchState = true;
            field.switchState = true;
          }
          return field;
        });
      }
    });
    this.savedFormObj = this.ccdForm.value;
  }

  trimFormFields() {
    Object.keys(this.ccdForm.controls).forEach(key => {
      const control = this.ccdForm.get(key);
      if (control && typeof control.value === 'string') {
        control.setValue(control.value.trim());
      }
    });
  }

  showError(field: string) {
    let errorMsg = this.validationService.showInputError(field, this.ccdForm);
    return errorMsg;
  }

  cancelManageCcd() {
    this.router.navigateByUrl('dashboard/review-ccd?ccdNo=' + this.ccdNo);
  }

  areAllRequiredFieldsFilled(): boolean {
    const allMandatoryFieldsFilled = this.departmentFields.filter(field => field?.realTimeSwitchState).every(field => {
      const control = this.ccdForm.get(field?.fieldName);
      return control && control.value && control.valid;
    });
    return allMandatoryFieldsFilled && this.ccdForm.valid && this.newlyAddedDepartments?.length > 0;
  }

  truncateString(value: string, limit: number): string {
    if (!value) return '';
    return value.length > limit ? value.substring(0, limit) + '...' : value;
  }

  submitPopup() {
    if(this.newlyAddedDepartments?.length > 0){
      this.openActivateDepartmentPopup = true;
    }
    else{
      this.onSubmit({isActivateDepartment: true});
    }
  }
  onSubmit(event:any) {
    this.openActivateDepartmentPopup = false;
    if (event?.isActivateDepartment && this.ccdForm.valid) {
      this.trimFormFields();
      const reqData = {
        ccdNo: this.ccdNo,
        departments: this.ccdForm?.value,
        type:'active',
        newlyAddedDepartments: this.newlyAddedDepartments?.length > 0 ? this.newlyAddedDepartments : []
      };
      this.submitLoading = true;
      this.ccdService.createCcdStep2(reqData).subscribe({
        next: ({message}: any) => {
          if(message){
            this.toasterService.openToastCcd(Toasts.Types.Success, message);
          }
          this.storageService.putDataInStorage('selectedTab', 'active');
          this.router.navigateByUrl('dashboard/review-ccd?ccdNo=' + this.ccdNo);
          this.submitLoading = false;
        },
        error: (error) => {
          console.error('Error creating/updating CCD:', error);
          if (error?.status === 423) {
            this.toasterService.openToastCcd(Toasts.Types.Warning, error?.error?.reason);
          } else {
            this.toasterService.openToastCcd(Toasts.Types.Error, Toasts.Actions.Submit.Error);
          }
          this.submitLoading = false;
        }
      });
    }
  }
  
  departmentSwitchClick(event:any, fieldName: string) {
    const formControl = this.ccdForm.get(fieldName);
    if(formControl){
      if(event?.isSwitchActive) {
        this.newlyAddedDepartments.push(fieldName);
        formControl?.addValidators([Validators.minLength(3), Validators.maxLength(100)]);
      } else {
        const index = this.newlyAddedDepartments.indexOf(fieldName);
        if (index > -1) {
          this.newlyAddedDepartments.splice(index, 1); // Remove 1 element at the found index
        }
        formControl?.patchValue('');
        formControl?.clearValidators();
      }
      formControl?.updateValueAndValidity();
    }
    const departmentFields = JSON.parse(JSON.stringify(this.departmentFields));
    this.departmentFields = [];
    this.departmentFields = departmentFields.map((field: any) => {
      if (field.fieldName === fieldName) {
        field.realTimeSwitchState = event?.isSwitchActive;
      }
      return field;
    });
  }
}
